(function() {
  'use strict';

  const DEBUG = window.JUNO_DEBUG === true;

  // Simple state object
  const state = {
    selectedSize: null,
    /** Last explicit size-button choice (Small–XL); used when pricing infers "Taster Pouch" from variant only. */
    previousSize: null,
    purchaseType: 'subscription', // 'subscription' | 'onetime60' | 'taster'
    oneTimeMode: 'taste', // legacy: 'taste' | 'full' (used when demoted anchor switches to onetime60)
    quantity: 1,
    selectedVariant: null,
    selectedSellingPlan: null,
    dogDetails: {},
    isSubmitting: false
  };

  /** Option labels used for RRP / 60-day pricing tiers (not the Taster product variant name). */
  const STANDARD_DOG_SIZES = ['Small', 'Medium', 'Large', 'Extra Large'];

  const POUCH_DAYS_BY_SIZE = {
    'Small': 60,
    'Medium': 30,
    'Large': 20,
    'Extra Large': 15,
    'Taster Pouch': 15
  };

  /** 60 Day Plan strikethrough vs buying taster pouches at list price (M/L/XL only in UI). */
  const ONETIME60_TASTER_STRIKE_MULTIPLIER = {
    'Medium': 2,
    'Large': 3,
    'Extra Large': 4
  };

  const CONFIG = window.JUNO_FORM_CONFIG || {};
  const TASTE_TEST_PRICE = (CONFIG.tasteTestPrice != null) ? CONFIG.tasteTestPrice : 39;
  const CART_ADD_THROTTLE_MS = 2000;
  let lastCartAddTime = 0;
  const RRP_60_DAY = CONFIG.rrp60DayBySize || {
    'Small': 39,
    'Medium': 65,
    'Large': 83,
    'Extra Large': 99
  };
  
  window.subscriptionFormState = state;

  const productData = window.productData;

  // Helper: Get subscription plan ID
  function getSubscriptionPlanId() {
    if (!productData?.selling_plan_groups) return null;
    const subGroup = productData.selling_plan_groups.find(g => 
      g.name.toLowerCase().includes('juno+') || g.name.toLowerCase().includes('subscription')
    );
    if (subGroup?.selling_plans?.length > 0) {
      const planId = subGroup.selling_plans[0].id;
      return typeof planId === 'string' ? parseInt(planId, 10) : planId;
    }
    if (productData.selling_plan_groups[0]?.selling_plans?.length > 0) {
      const planId = productData.selling_plan_groups[0].selling_plans[0].id;
      return typeof planId === 'string' ? parseInt(planId, 10) : planId;
    }
    return null;
  }

  function formatMoney(cents) {
    if (cents == null || isNaN(cents)) return '£0.00';
    return '£' + (cents / 100).toFixed(2);
  }

  function formatPounds(pounds) {
    if (pounds == null || isNaN(pounds)) return '£0.00';
    const p = Number(pounds);
    return '£' + p.toFixed(2);
  }

  function formatPoundsOrPence(pounds) {
    if (pounds == null || isNaN(pounds)) return '0p';
    const p = Number(pounds);
    if (p > 0 && p < 1) {
      return Math.round(p * 100) + 'p';
    }
    return formatPounds(pounds);
  }

  // Load dog details from quiz/sessionStorage/URL (junoPetData has 7-day expiry for security)
  function getPetDataWithExpiry() {
    try {
      const stored = localStorage.getItem('junoPetData') || sessionStorage.getItem('junoPetData');
      if (!stored) return null;
      const data = JSON.parse(stored);
      const ts = data.timestamp;
      if (ts && (Date.now() - ts) > 7 * 24 * 60 * 60 * 1000) {
        localStorage.removeItem('junoPetData');
        sessionStorage.removeItem('junoPetData');
        return null;
      }
      return data;
    } catch (e) {
      return null;
    }
  }

  // A quiz left part-way (no email yet) never writes junoPetData, which elsewhere means
  // "quiz finished". Read its saved answers directly so the dog's name and breed still
  // carry over to this form. Same 7-day window the quiz uses to resume.
  function getPartialQuizAnswers() {
    try {
      const saved = JSON.parse(localStorage.getItem('junoQuizState') || 'null');
      if (!saved || !saved.timestamp || (Date.now() - saved.timestamp) > 7 * 24 * 60 * 60 * 1000) return null;
      return {
        name: saved.name || '',
        breed: saved.breed || '',
        age: saved.age || '',
        size: saved.weight || '',
        weight: saved.weight || '',
        issues: Array.isArray(saved.issues) ? saved.issues.join(', ') : (saved.issues || ''),
        notes: saved.notes || ''
      };
    } catch (e) {
      return null;
    }
  }

  function loadDogDetails() {
    const urlParams = new URLSearchParams(window.location.search);
    const quizData = window.junoQuizAutofillData || {};
    const sessionData = getPetDataWithExpiry() || getPartialQuizAnswers() || {};
    
    function capitalizeName(name) {
      return formatDogName(name);
    }
    
    const details = ['name', 'breed', 'age', 'weight', 'size', 'issues', 'notes'].reduce((acc, key) => {
      const altKey = key === 'name' ? 'dogName' : key;
      let value = urlParams.get(key) || urlParams.get(altKey) || 
                  quizData[key] || quizData[altKey] || 
                  sessionData[key] || sessionData[altKey] || '';
      if (typeof value === 'string') value = value.trim();
      else if (value != null && value !== '') value = String(value).trim();
      else value = '';
      if (key === 'name' && value) value = capitalizeName(value);
      acc[key] = value;
      return acc;
    }, {});
    
    if (!details.weight && details.size) details.weight = details.size;
    if (!details.weight && sessionData.size) details.weight = sessionData.size;
    if (!details.weight && urlParams.get('size')) {
      details.weight = urlParams.get('size').trim();
      if (!details.size) details.size = details.weight;
    }
    
    if (Object.values(details).some(v => v)) {
      state.dogDetails = details;
      return details;
    }
    return {};
  }

  // Save pet data to localStorage (7-day expiry for security)
  function savePetDataToStorage() {
    try {
      const mainName = document.getElementById('property-dog-name');
      const mainBreed = document.getElementById('property-dog-breed');
      const name = (mainName?.value || state.dogDetails?.name || '').trim();
      const breed = (mainBreed?.value || state.dogDetails?.breed || '').trim();
      if (!name && !breed) return;
      const existing = getPetDataWithExpiry() || {};
      const petData = {
        ...existing,
        name: name || existing.name,
        breed: breed || existing.breed,
        size: state.selectedSize || existing.size,
        weight: state.selectedSize || existing.weight,
        timestamp: Date.now()
      };
      if (petData.name || petData.breed || petData.size) {
        localStorage.setItem('junoPetData', JSON.stringify(petData));
        sessionStorage.setItem('junoPetData', JSON.stringify(petData));
      }
    } catch (e) {
      console.warn('Failed to save pet data:', e);
    }
  }

  // Pre-fill inputs from quiz data
  function prefillInputs(details) {
    if (!details) return;
    
    function capitalizeName(name) {
      return formatDogName(name);
    }
    
    function updateCheckmark(input) {
      const inputGroup = input?.closest('.pet-input-group');
      if (input && input.value.trim().length > 0) {
        input.classList.add('filled');
        if (inputGroup) inputGroup.classList.add('has-filled');
      } else {
        input.classList.remove('filled');
        if (inputGroup) inputGroup.classList.remove('has-filled');
      }
    }
    
    const nameInput = document.getElementById('property-dog-name');
    const breedInput = document.getElementById('property-dog-breed');
    const ageInput = document.getElementById('property-dog-age');
    const sizeInput = document.getElementById('property-dog-size');
    const issuesInput = document.getElementById('property-dog-issues');
    const notesInput = document.getElementById('property-dog-notes');
    
    if (nameInput && details.name) {
      nameInput.value = capitalizeName(details.name);
      updateCheckmark(nameInput);
    }
    if (breedInput && details.breed) {
      breedInput.value = details.breed;
      updateCheckmark(breedInput);
    }
    if (ageInput && details.age) ageInput.value = details.age;
    if (sizeInput && details.size) sizeInput.value = details.size;
    if (issuesInput && details.issues) {
      const issuesStr = Array.isArray(details.issues) ? details.issues.join(', ') : details.issues;
      issuesInput.value = issuesStr;
    }
    if (notesInput && details.notes) notesInput.value = details.notes;
  }

  // Auto-select size from quiz data, saved pet data or a ?size= / ?weight= link.
  // Must run after init() binds the size button click handler, or the click does nothing.
  // Age is not a size hint: it is in years, so "5" would wrongly infer Small.
  function autoSelectSize(details) {
    if (!details) return;
    const sizeHint = details.size || details.weight;
    if (!sizeHint) return;
    
    const sizeButtons = document.querySelectorAll('.size-btn');
    if (sizeButtons.length === 0) return;
    
    const target = Array.from(sizeButtons).find(b => 
      b.dataset.size?.toLowerCase() === sizeHint.toLowerCase()
    );
    
    if (target) {
      target.click();
      return;
    }
    
    // Try to infer from numeric weight
    const numeric = parseFloat(sizeHint);
    if (!Number.isNaN(numeric)) {
      let inferred = null;
      if (numeric < 10) inferred = 'Small';
      else if (numeric < 20) inferred = 'Medium';
      else if (numeric < 30) inferred = 'Large';
      else inferred = 'Extra Large';
      
      const inferredTarget = Array.from(sizeButtons).find(b => 
        b.dataset.size?.toLowerCase() === inferred?.toLowerCase()
      );
      if (inferredTarget) inferredTarget.click();
    }
  }


  // Select variant based on size
  function selectVariant() {
    if (!productData?.variants) return;
    
    let variant = null;
    
    // For Taster mode, always try to select the dedicated Taster Pouch variant,
    // while still keeping the dog's size in state.selectedSize for pricing/day logic.
    if (state.purchaseType === 'taster') {
      variant = productData.variants.find(v =>
        v.title === 'Taster Pouch' ||
        v.option1 === 'Taster Pouch' ||
        v.option2 === 'Taster Pouch' ||
        v.option3 === 'Taster Pouch'
      );
      // Fallback: if for some reason there is no dedicated Taster variant,
      // fall back to the size-based variant selection.
      if (!variant && state.selectedSize) {
        variant = productData.variants.find(v =>
          v.title === state.selectedSize || v.option1 === state.selectedSize
        );
      }
    } else {
      if (!state.selectedSize) return;
      variant = productData.variants.find(v =>
        v.title === state.selectedSize || v.option1 === state.selectedSize
      );
    }
    
    if (!variant) {
      showError('This option is currently unavailable');
      return;
    }
    
    if (!variant.available) {
      showError('This option is currently sold out');
      const addToCartBtns = document.querySelectorAll('.add-to-cart-btn');
      addToCartBtns.forEach(btn => {
        btn.disabled = true;
        const label = btn.querySelector('.btn-label');
        if (label) label.textContent = 'Sold Out';
      });
      return;
    }
    
    state.selectedVariant = variant;
    const variantInput = document.getElementById('variant-id-input');
    if (variantInput) variantInput.value = variant.id;
    
    // Handle selling plan (subscription only; onetime60 and taster have no plan)
    if (state.purchaseType === 'subscription') {
      const planId = getSubscriptionPlanId();
      if (planId) {
        state.selectedSellingPlan = planId;
        const planInput = document.getElementById('selling-plan-input');
        if (planInput) planInput.value = planId;
      }
    } else {
      state.selectedSellingPlan = null;
      const planInput = document.getElementById('selling-plan-input');
      if (planInput) planInput.value = '';
    }
    
    hideError();
    updatePricing();
    
    // Dispatch state change event for banner
    window.dispatchEvent(new CustomEvent('juno:formStateChange', { 
      detail: {
        ...state,
        selectedSize: state.selectedSize,
        selectedVariant: state.selectedVariant,
        purchaseType: state.purchaseType,
        quantity: state.quantity
      }
    }));
  }

  // Get effective size for pricing (infer from variant if not yet selected)
  function getPricingSize() {
    if (state.selectedSize) return state.selectedSize;
    if (state.selectedVariant?.title) return state.selectedVariant.title;
    if (state.selectedVariant?.option1) return state.selectedVariant.option1;
    return 'Medium';
  }

  function normalizeStandardDogSize(label) {
    if (!label || typeof label !== 'string') return null;
    const t = label.trim();
    const found = STANDARD_DOG_SIZES.find(function (s) {
      return s.toLowerCase() === t.toLowerCase();
    });
    return found || null;
  }

  /** Map quiz / pet fields to a standard tier when the user has not picked a size button yet. */
  function inferStandardSizeFromDogDetails() {
    const d = state.dogDetails;
    if (!d) return null;
    const hint = (d.size || d.weight || '').trim();
    if (!hint) return null;
    const fromLabel = normalizeStandardDogSize(hint);
    if (fromLabel) return fromLabel;
    const numeric = parseFloat(hint);
    if (!Number.isNaN(numeric)) {
      if (numeric < 10) return 'Small';
      if (numeric < 20) return 'Medium';
      if (numeric < 30) return 'Large';
      return 'Extra Large';
    }
    return null;
  }

  /**
   * Tier used for RRP_60_DAY and taster savings (never "Taster Pouch").
   * When only the Taster variant is known, use last size click, then quiz inference, then Medium.
   */
  function getRrpTierSize() {
    const pricingLabel = getPricingSize();
    const normalized = normalizeStandardDogSize(pricingLabel);
    if (normalized) return normalized;
    const labelIsTasterPouch = typeof pricingLabel === 'string' &&
      pricingLabel.trim().toLowerCase() === 'taster pouch';
    if (labelIsTasterPouch || !RRP_60_DAY[pricingLabel]) {
      if (state.previousSize && normalizeStandardDogSize(state.previousSize)) {
        return state.previousSize;
      }
      const inferred = inferStandardSizeFromDogDetails();
      if (inferred) return inferred;
      return 'Medium';
    }
    return pricingLabel;
  }

  function variantIsTasterPouch(v) {
    if (!v) return false;
    return v.title === 'Taster Pouch' ||
      v.option1 === 'Taster Pouch' ||
      v.option2 === 'Taster Pouch' ||
      v.option3 === 'Taster Pouch';
  }

  /** 60-day supply variant for a standard size tier (never the Taster Pouch SKU). */
  function findStandardSizeVariant(tierSize) {
    if (!tierSize || !productData?.variants) return null;
    return productData.variants.find(function (v) {
      if (variantIsTasterPouch(v)) return false;
      return v.title === tierSize || v.option1 === tierSize ||
        v.option2 === tierSize || v.option3 === tierSize;
    }) || null;
  }

  /**
   * Canonical 60-day one-time £ for the dog's size tier — used for all plan rows and Juno+ maths
   * so toggling Taster / Subscribe does not rewrite numbers from a second pricing source.
   */
  function getSixtyDayTierOneTimePounds() {
    const sizeFor60Day = getRrpTierSize();
    const v = findStandardSizeVariant(sizeFor60Day);
    if (v != null && v.price != null) {
      const cents = Number(v.price);
      if (!isNaN(cents)) {
        return cents / 100;
      }
    }
    return RRP_60_DAY[sizeFor60Day] || 65;
  }

  // Get one-time 60-day cost and Juno+ price (20% off) for the size tier
  function getOneTimeAndSubPrice() {
    const oneTimeCost = getSixtyDayTierOneTimePounds();
    const subPrice = Math.round(oneTimeCost * 0.8 * 100) / 100;
    return { oneTimeCost, subPrice };
  }

  // Update pricing display - Juno+ is 20% off the one-time cost for each size
  function updatePricing() {
    if (!state.selectedVariant) return;
    
    const size = getPricingSize();
    const { oneTimeCost, subPrice } = getOneTimeAndSubPrice();
    const rrp60 = oneTimeCost;
    /* Subscription card always shows per-60-day price (1 unit). state.quantity is for one-time pouch count. */
    const subDisplayQty = state.purchaseType === 'subscription' ? state.quantity : 1;
    const billedValue = Math.round(subPrice * subDisplayQty * 100) / 100; /* billed every 60 days */
    const monthlyPrice = Math.round((billedValue / 2) * 100) / 100;

    /* Main: £52 every 60 days (whole amount, 60-day billing) */
    const subPriceDisplay = document.querySelector('.subscription-price-display');
    if (subPriceDisplay) {
      subPriceDisplay.textContent = formatPounds(billedValue);
    }

    const subPricePeriod = document.getElementById('subscription-price-period');
    if (subPricePeriod) {
      subPricePeriod.textContent = ' / 60 days';
    }

    const subStrikePrice = document.getElementById('subscription-strike-price');
    if (subStrikePrice) {
      subStrikePrice.textContent = formatPounds(rrp60 * subDisplayQty);
    }

    const dailyPounds = billedValue / 60;
    const dailyText = `${formatPoundsOrPence(dailyPounds)} / day`;
    document.querySelectorAll('.subscription-daily-display').forEach(function (el) {
      el.textContent = dailyText;
    });

    /* Below: monthly cost (swapped from billed amount) */
    document.querySelectorAll('.subscription-monthly-cost').forEach(function(el) {
      el.textContent = formatPounds(monthlyPrice);
    });
    
    // 60 Day Plan card: always full 60-day one-time (Taster is a separate radio)
    const onetime60PriceDisplay = document.getElementById('onetime60-price-display');
    const onetime60StrikePrice = document.getElementById('onetime60-strike-price');
    const onetime60DailyDisplay = document.getElementById('onetime60-daily-display');
    const onetimeCardTitle = document.getElementById('onetime-card-title');
    const onetimeCardSupply = document.getElementById('onetime-card-supply');
    const onetimeCardSupplyInline = document.getElementById('onetime-card-supply-inline');
    const pouchDays = POUCH_DAYS_BY_SIZE[size] || 15;

    if (onetime60PriceDisplay) onetime60PriceDisplay.textContent = formatPounds(rrp60);
    if (onetime60StrikePrice) {
      const strikeMult = state.selectedSize ? ONETIME60_TASTER_STRIKE_MULTIPLIER[state.selectedSize] : null;
      if (strikeMult != null) {
        const strikePounds = Math.round(TASTE_TEST_PRICE * strikeMult * 100) / 100;
        onetime60StrikePrice.textContent = formatPounds(strikePounds);
        onetime60StrikePrice.hidden = false;
      } else {
        onetime60StrikePrice.textContent = '';
        onetime60StrikePrice.hidden = true;
      }
    }
    if (onetime60DailyDisplay) {
      const daily60 = rrp60 / 60;
      onetime60DailyDisplay.textContent = `${formatPoundsOrPence(daily60)} / day`;
    }
    if (onetimeCardTitle) {
      const onetimeLead = onetimeCardTitle.querySelector('.onetime-title-line--lead');
      const onetimeTail = onetimeCardTitle.querySelector('.onetime-title-line--tail');
      if (onetimeLead) onetimeLead.textContent = 'One-Time';
      if (onetimeTail) onetimeTail.textContent = 'Purchase';
    }
    if (onetimeCardSupply) onetimeCardSupply.textContent = '60-day supply';
    if (onetimeCardSupplyInline) onetimeCardSupplyInline.textContent = '60-day supply';

    // Taster Pouch card: £/day = pouch price ÷ days for selected size (hidden until size chosen; shown for all sizes)
    const tasterDays = state.selectedSize ? (POUCH_DAYS_BY_SIZE[state.selectedSize] || 15) : 30;
    const dailyTasterRate = TASTE_TEST_PRICE / tasterDays;
    const tasterPriceDisplay = document.getElementById('taster-price-display');
    const tasterDailyDisplay = document.getElementById('taster-daily-display');
    const tasterCardTitle = document.getElementById('taster-card-title');
    const tasterCardSupply = document.getElementById('taster-card-supply');
    const tasterCardSupplyInline = document.getElementById('taster-card-supply-inline');
    if (tasterPriceDisplay) tasterPriceDisplay.textContent = formatPounds(TASTE_TEST_PRICE);
    if (tasterDailyDisplay) tasterDailyDisplay.textContent = `${formatPoundsOrPence(dailyTasterRate)} / day`;
    if (tasterCardTitle) {
      const tasterLead = tasterCardTitle.querySelector('.onetime-title-line--lead');
      const tasterTail = tasterCardTitle.querySelector('.onetime-title-line--tail');
      if (tasterLead) tasterLead.textContent = 'Taster Pouch';
      if (tasterTail) tasterTail.textContent = `(${tasterDays} days)`;
      tasterCardTitle.setAttribute('aria-label', `Taster Pouch (${tasterDays} days)`);
    }
    if (tasterCardSupply) tasterCardSupply.textContent = `${tasterDays}-day supply`;
    if (tasterCardSupplyInline) tasterCardSupplyInline.textContent = `${tasterDays}-day supply`;
    
    const annualSavingsEl = document.querySelector('.annual-savings-text');
    if (annualSavingsEl) {
      const perOrderSaving = rrp60 - subPrice;
      const ordersPerYear = 6;
      const totalAnnualSaving = Math.round(perOrderSaving * ordersPerYear * 100) / 100;
      annualSavingsEl.textContent = `Save ${formatPounds(totalAnnualSaving)}/year`;
    }
    
    updateOnetimeWarningText();
    updateDosageMessage();
    updateButtonLabels();
    updatePurchaseOptionCardsVisibility();
    
    const addToCartBtns = document.querySelectorAll('.add-to-cart-btn');
    addToCartBtns.forEach(btn => {
      btn.classList.toggle('is-subscribe', state.purchaseType === 'subscription');
      btn.classList.toggle('is-onetime', state.purchaseType !== 'subscription');
      btn.disabled = !state.selectedVariant || !state.selectedVariant.available;
    });
  }

  /**
   * Small: Subscribe + Taster Pouch only (hide 60 Day Plan).
   * Medium / Large / Extra Large: Subscribe + 60 Day Plan + Taster Pouch.
   * Before a size is chosen, show Subscribe + Taster so shoppers can compare prices; hide 60 Day Plan only.
   */
  function updatePurchaseOptionCardsVisibility() {
    const onetimeLabel = document.getElementById('onetime-card-label');
    const tasterLabel = document.getElementById('taster-card-label');
    const size = state.selectedSize;
    if (!size) {
      if (onetimeLabel) onetimeLabel.style.display = 'none';
      if (tasterLabel) tasterLabel.style.display = '';
      return;
    }
    const show60Day = size !== 'Small';
    if (onetimeLabel) onetimeLabel.style.display = show60Day ? '' : 'none';
    if (tasterLabel) tasterLabel.style.display = '';
  }

  function updateOnetimeWarningText() {
    const textEl = document.querySelector('.onetime-warning-text');
    const labelEl = document.querySelector('.onetime-warning-label');
    if (!textEl) return;
    const size = getPricingSize();
    const pouchDays = POUCH_DAYS_BY_SIZE[size] || 15;
    const { oneTimeCost, subPrice } = getOneTimeAndSubPrice();
    const rrp60 = oneTimeCost;
    const junoPlusAnnual = subPrice * 6;

    let annualSavings = 0;
    if (state.purchaseType === 'taster') {
      const tasterAnnualCost = TASTE_TEST_PRICE * (365 / pouchDays);
      const shippingAnnual = 4.95 * 12;
      annualSavings = Math.max(0, Math.round((tasterAnnualCost + shippingAnnual - junoPlusAnnual) * 100) / 100);
    } else if (state.purchaseType === 'onetime60') {
      const onetimeAnnualCost = rrp60 * 6 + 4.95 * 6;
      annualSavings = Math.max(0, Math.round((onetimeAnnualCost - junoPlusAnnual) * 100) / 100);
    }

    const nameInput = document.getElementById('property-dog-name');
    const dogName = (state.dogDetails?.name || nameInput?.value || '').trim();
    const whoSaves = dogName ? capName(dogName) + ' could' : 'You could';

    if (labelEl) labelEl.textContent = 'Are you sure?';
    textEl.innerHTML = 'Juno+ members save 20% off every order, and get free shipping for life. ' + whoSaves + ' save over <span id="onetime-warning-savings">' + formatPounds(annualSavings) + '</span> per year.';
  }

  function updateDosageMessage() {
    const dosageText = document.getElementById('dosage-text');
    const dosageMessage = document.querySelector('.dosage-message');

    if (!dosageText || !dosageMessage) return;
    const size = getPricingSize();
    if (!size) return;

    const nameInput = document.getElementById('property-dog-name');
    const rawName = (nameInput?.value || state.dogDetails?.name || '').trim();
    const displayNameSub = rawName ? escapeHtml(capName(rawName)) : 'Your dog';
    const displayNameSubContinue = rawName ? escapeHtml(capName(rawName)) : 'your dog';

    // Dynamic taster note (name-aware). Shown via CSS only when taster is selected.
    const tasterFlavorNoteText = document.getElementById('taster-flavor-note-text');
    if (tasterFlavorNoteText) {
      const tasterLeadName = rawName ? capName(rawName) : 'your dog';
      tasterFlavorNoteText.textContent =
        `Ideal for confirming ${tasterLeadName} loves the flavour. For joint, gut and skin support, a minimum 60-day window is recommended to allow active nutrients to reach therapeutic levels.`;
    }

    const tasterPouchDays = state.selectedSize
      ? (POUCH_DAYS_BY_SIZE[state.selectedSize] || 15)
      : (POUCH_DAYS_BY_SIZE[size] || 15);
    const scoopBySize = {
      Small: { amount: '0.5', word: 'scoops' },
      Medium: { amount: '1', word: 'scoop' },
      Large: { amount: '1.5', word: 'scoops' },
      'Extra Large': { amount: '2', word: 'scoops' }
    };
    const { amount, word } = scoopBySize[size] || { amount: '1', word: 'scoop' };

    // Total scoops across the 60-day supply (matches 30 / 60 / 90 / 120 by size).
    const totalScoops60 = Math.round(parseFloat(amount) * 60);

    const supplyMessage =
      `<strong>Perfect! <span class="dosage-dog-name dosage-dog-name--sentence">${displayNameSub}</span> needs <span class="scoops-per-day">${amount}</span> <span class="scoop-word">${word}</span> per day.</strong><br>` +
      `A 60 day supply (${totalScoops60} scoops) allows active nutrients to build up in the system for visible results in the joints and gut.`;

    if (state.purchaseType === 'subscription' || state.purchaseType === 'onetime60') {
      dosageText.innerHTML = supplyMessage;
    } else if (state.purchaseType === 'taster') {
      dosageText.innerHTML =
        `Perfect! <span class="dosage-dog-name dosage-dog-name--sentence">${displayNameSub}</span> needs <strong class="scoops-per-day">${amount}</strong> <span class="scoop-word">${word}</span> per day, the pouch will last <span class="dosage-dog-name dosage-dog-name--continue">${displayNameSubContinue}</span> <strong>${tasterPouchDays}</strong> days.`;
    }
  }

  function updateButtonLabels() {
    const size = getPricingSize();
    if (!size) return;
    const { oneTimeCost, subPrice } = getOneTimeAndSubPrice();
    const rrp = oneTimeCost;
    let priceString = formatPounds(subPrice);
    if (state.purchaseType === 'taster') priceString = formatPounds(TASTE_TEST_PRICE);
    else if (state.purchaseType === 'onetime60') priceString = formatPounds(rrp);
    
    const rawDogName = state.dogDetails?.name || '';
    const dogPossessive = rawDogName
      ? `${capName(rawDogName)}'s`
      : "your dog's";
    const dogPossessiveSafe = escapeHtml(dogPossessive);
    const btnLabels = document.querySelectorAll('.btn-label');
    
    btnLabels.forEach(label => {
      let actionVerb = '';

      if (state.purchaseType === 'subscription') {
        actionVerb = `Start ${dogPossessiveSafe} 60 Day Plan`;
      } else if (state.purchaseType === 'onetime60') {
        actionVerb = `Get ${dogPossessiveSafe} 60 Day Pack`;
      } else {
        actionVerb = `Try ${dogPossessiveSafe} Taster Pouch`;
      }
      
      label.innerHTML = actionVerb;
    });
  }

  // Blocked words for dog name (no expletives or inappropriate content)
  const DOG_NAME_BLOCKED = ['ass', 'arse', 'bollocks', 'bugger', 'bloody', 'crap', 'damn', 'dick', 'fuck', 'fck', 'fuk', 'shit', 'piss', 'bastard', 'bitch', 'slut', 'whore', 'cunt', 'dumbass', 'shithead', 'asshole', 'dipshit', 'bullshit', 'dickhead', 'wanker', 'twat', 'prick', 'cock', 'tit', 'tits', 'wtf', 'stfu', 'omfg', 'lmfao', 'fck', 'fuk', 'f*ck', 'sht', 'sh1t'];

  function escapeRegexDogName(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function isNameInappropriate(name) {
    if (!name || typeof name !== 'string') return false;
    const lower = name.trim().toLowerCase();
    if (!lower) return false;
    const nospace = lower.replace(/\s+/g, '');
    const segments = [lower, ...lower.split(/\s+/), nospace];
    for (let i = 0; i < DOG_NAME_BLOCKED.length; i++) {
      const blocked = DOG_NAME_BLOCKED[i];
      const re = new RegExp('\\b' + escapeRegexDogName(blocked) + '\\b', 'i');
      for (let s = 0; s < segments.length; s++) {
        if (re.test(segments[s])) return true;
      }
    }
    return false;
  }

  function showError(message) {
    const errorText = document.querySelector('.error-text');
    const errorMessage = document.querySelector('.error-message');
    if (errorText) errorText.textContent = message;
    if (errorMessage) errorMessage.style.display = 'flex';
  }

  function hideError() {
    const errorMessage = document.querySelector('.error-message');
    if (errorMessage) errorMessage.style.display = 'none';
  }

  // Shared dog-name casing rule (same rule in utils.js, juno-product-autofill.js, the quiz and the header).
  function formatDogName(name) {
    if (!name || typeof name !== 'string') return name;
    var trimmed = name.trim();
    if (trimmed.length === 0) return trimmed;
    // Typed in one case ("bella rose", "BELLA"): capitalise each word, split on spaces and hyphens,
    // plus the letter after a single-letter prefix like O' or D' ("o'malley" -> "O'Malley").
    if (trimmed === trimmed.toLowerCase() || trimmed === trimmed.toUpperCase()) {
      return trimmed.toLowerCase().split(/([\s-]+)/).map(function(part) {
        if (!part || /^[\s-]+$/.test(part)) return part;
        var first = part.charAt(0);
        part = first.toUpperCase() + part.slice(1);
        if (part.length > 2 && (part.charAt(1) === "'" || part.charAt(1) === '\u2019') && first.toLowerCase() !== first.toUpperCase()) {
          part = part.slice(0, 2) + part.charAt(2).toUpperCase() + part.slice(3);
        }
        return part;
      }).join('');
    }
    // Mixed case ("McFly", "Bella Rose"): keep exactly what they typed, just start with a capital.
    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
  }

  function capName(str) {
    return formatDogName(str);
  }

  // Escape user input for safe HTML insertion (prevents XSS)
  function escapeHtml(unsafe) {
    if (unsafe == null || typeof unsafe !== 'string') return '';
    return String(unsafe)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function initBreedDropdown() {
    const breedInput = document.getElementById('property-dog-breed');
    const breedDropdown = document.getElementById('subscription-breed-dropdown');
    const breedItems = breedDropdown ? Array.from(breedDropdown.querySelectorAll('.breed-dropdown-item')) : [];
    let selectedIndex = -1;
    let filteredItems = breedItems.slice();

    if (!breedInput || !breedDropdown || breedItems.length === 0) return;

    function showDropdown() {
      breedDropdown.style.display = 'block';
      breedInput.setAttribute('aria-expanded', 'true');
    }

    function hideDropdown() {
      breedDropdown.style.display = 'none';
      breedInput.setAttribute('aria-expanded', 'false');
      selectedIndex = -1;
      breedItems.forEach(function(item) {
        item.style.backgroundColor = '';
      });
    }

    function highlightItem(index) {
      breedItems.forEach(function(item) {
        item.style.backgroundColor = '';
      });
      if (index >= 0 && index < filteredItems.length) {
        filteredItems[index].style.backgroundColor = '#f4f8f6';
        filteredItems[index].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }

    function filterBreeds(searchTerm) {
      const term = String(searchTerm || '').toLowerCase().trim();
      filteredItems = [];

      breedItems.forEach(function(item) {
        const breedName = (item.getAttribute('data-breed') || '').toLowerCase();
        if (!term || breedName.indexOf(term) !== -1) {
          item.style.display = 'block';
          filteredItems.push(item);
        } else {
          item.style.display = 'none';
        }
      });

      selectedIndex = -1;
      highlightItem(selectedIndex);

      if (document.activeElement === breedInput && filteredItems.length > 0) {
        showDropdown();
      } else {
        hideDropdown();
      }
    }

    function selectBreed(item) {
      const breedName = item.getAttribute('data-breed') || item.textContent || '';
      breedInput.value = breedName;
      hideDropdown();
      breedInput.dispatchEvent(new Event('input', { bubbles: true }));
    }

    breedInput.addEventListener('input', function(e) {
      filterBreeds(e.target.value);
    });

    breedInput.addEventListener('focus', function() {
      filterBreeds(breedInput.value);
      if ((breedInput.value || '').trim().length === 0 && filteredItems.length > 0) {
        showDropdown();
      }
    });

    breedInput.addEventListener('keydown', function(e) {
      if (breedDropdown.style.display !== 'block' || filteredItems.length === 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        selectedIndex = (selectedIndex + 1) % filteredItems.length;
        highlightItem(selectedIndex);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        selectedIndex = selectedIndex <= 0 ? filteredItems.length - 1 : selectedIndex - 1;
        highlightItem(selectedIndex);
      } else if (e.key === 'Enter' && selectedIndex >= 0) {
        e.preventDefault();
        selectBreed(filteredItems[selectedIndex]);
      } else if (e.key === 'Escape') {
        hideDropdown();
      }
    });

    breedItems.forEach(function(item) {
      item.addEventListener('mouseenter', function() {
        const idx = filteredItems.indexOf(item);
        if (idx >= 0) {
          selectedIndex = idx;
          highlightItem(selectedIndex);
        }
      });

      item.addEventListener('mousedown', function(e) {
        // Prevent blur before click selection.
        e.preventDefault();
      });

      item.addEventListener('click', function(e) {
        e.preventDefault();
        selectBreed(item);
      });
    });

    document.addEventListener('click', function(e) {
      if (e.target !== breedInput && !breedDropdown.contains(e.target)) {
        hideDropdown();
      }
    });
  }

  // Update personalized titles from form name/breed (quiz or manual input)
  function updatePersonalizedTitles() {
    const dogName = (state.dogDetails?.name || '').trim();
    const sizeTitle = document.getElementById('size-section-title');
    const planTitle = document.getElementById('plan-section-title');
    const dogDetailsTitle = document.getElementById('dog-details-title');
    const dosageSentence = dogName ? capName(dogName) : 'Your dog';
    const dosageContinue = dogName ? capName(dogName) : 'your dog';
    document.querySelectorAll('.dosage-dog-name--sentence').forEach(function (el) {
      el.textContent = dosageSentence;
    });
    document.querySelectorAll('.dosage-dog-name--continue').forEach(function (el) {
      el.textContent = dosageContinue;
    });
    const dosageNameLegacy = document.getElementById('dosage-dog-name');
    if (dosageNameLegacy) dosageNameLegacy.textContent = dosageSentence;

    if (dogName) {
      const name = capName(dogName);
      if (sizeTitle) sizeTitle.textContent = "Step 2: " + name + "'s Size";
      if (planTitle) planTitle.textContent = "Step 3: " + name + "'s Plan";
      if (dogDetailsTitle) dogDetailsTitle.textContent = "Step 1: " + name + "'s Details";
    } else {
      if (sizeTitle) sizeTitle.textContent = "Step 2: Select Your Dog's Size";
      if (planTitle) planTitle.textContent = "Step 3: Your Dog's Plan";
      if (dogDetailsTitle) dogDetailsTitle.textContent = "Step 1: Your Dog's Details";
    }
  }

  // Update rest of page (Juno Standard for breed, name in headings, etc.) like post-quiz
  function updateRestOfPagePersonalization() {
    if (typeof window.personalization !== 'undefined') {
      const name = (state.dogDetails?.name || '').trim();
      const breed = (state.dogDetails?.breed || '').trim();
      const capitalizedName = name ? capName(name) : '';
      if (capitalizedName) {
        window.personalization.updateTitle(capitalizedName);
        window.personalization.updateHeadings(capitalizedName);
        window.personalization.updateFuelBestLife(capitalizedName);
        window.personalization.updateFoundationBestLife(capitalizedName);
      }
      if (breed) {
        window.personalization.updateIngredientsHeading(breed);
        window.personalization.updateJunoStandard(breed);
      }
    }
  }

  // Handle size selection
  function handleSizeClick(btn) {
    const size = btn.dataset.size;
    const scoops = parseFloat(btn.dataset.scoops);
    
    state.selectedSize = size;
    state.previousSize = size;
    // Small has no 60 Day Plan; if user had it selected on a larger size, move to subscription
    if (size === 'Small' && state.purchaseType === 'onetime60') {
      const subRadio = document.getElementById('purchase-type-subscription');
      if (subRadio) {
        subRadio.checked = true;
        handlePurchaseTypeChange(subRadio);
      }
    }

    const dogSizeInput = document.getElementById('property-dog-size');
    if (dogSizeInput) dogSizeInput.value = size;
    
    const sizeButtons = document.querySelectorAll('.size-btn');
    sizeButtons.forEach(b => {
      b.classList.remove('active', 'error-state');
    });
    btn.classList.add('active');
    
    const dosageMessage = document.querySelector('.dosage-message');
    const pleaseSelectMessage = document.querySelector('.please-select-message');
    
    if (dosageMessage) dosageMessage.style.display = 'flex';
    if (pleaseSelectMessage) pleaseSelectMessage.style.display = 'none';
    
    const formContainer = document.querySelector('.juno-subscription-form-v3');
    if (formContainer) formContainer.classList.add('size-selected');
    
    if (state.purchaseType === 'subscription') {
      state.quantity = 1;
      const qtyDisplayInputs = document.querySelectorAll('.qty-display');
      qtyDisplayInputs.forEach(input => input.value = 1);
      const quantityInput = document.getElementById('quantity-input');
      if (quantityInput) quantityInput.value = 1;
    } else if (state.purchaseType === 'onetime60') {
      setQuantityForMode();
    }
    
    selectVariant();
    
    // Dispatch state change event for banner and other listeners
    window.dispatchEvent(new CustomEvent('juno:formStateChange', { 
      detail: {
        ...state,
        selectedSize: state.selectedSize,
        selectedVariant: state.selectedVariant,
        purchaseType: state.purchaseType,
        quantity: state.quantity
      }
    }));
  }

  // Get Appstle selling_plan inputs (exclude our hidden #selling-plan-input)
  function getAppstleSellingPlanInputs() {
    const inputs = document.querySelectorAll('input[name="selling_plan"]');
    return Array.from(inputs).filter(function(el) { return el.id !== 'selling-plan-input'; });
  }

  // Get the currently checked Appstle selling plan ID (from the checked radio), or null for onetime
  function getCheckedAppstleSellingPlanId() {
    const appstleInputs = getAppstleSellingPlanInputs();
    const checked = appstleInputs.find(function(r) { return r.checked && r.value && String(r.value).trim() !== ''; });
    return checked && checked.value ? String(checked.value).trim() : null;
  }

  // Highlight size section when user needs to select (no size selected yet)
  function highlightSizeSection() {
    if (state.selectedSize) return;
    const sizeSection = document.querySelector('.size-section');
    const pleaseSelectMessage = document.querySelector('.please-select-message');
    const sizeButtons = document.querySelectorAll('.size-btn');
    if (pleaseSelectMessage) pleaseSelectMessage.style.display = 'inline';
    sizeButtons.forEach(btn => btn.classList.add('error-state'));
    if (sizeSection) {
      sizeSection.classList.add('wiggle');
      setTimeout(() => sizeSection.classList.remove('wiggle'), 400);
      setTimeout(() => sizeButtons.forEach(btn => btn.classList.remove('error-state')), 600);
      const offset = window.innerWidth < 768 ? 120 : 160;
      const targetPosition = sizeSection.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top: Math.max(targetPosition, 0), behavior: 'smooth' });
    }
  }

    // Handle purchase type change: programmatically control hidden Appstle widget
  function handlePurchaseTypeChange(radio) {
    state.purchaseType = radio.value;
    if (state.purchaseType === 'taster') {
      state.quantity = 1;
      const qtyDisplayInputs = document.querySelectorAll('.qty-display');
      qtyDisplayInputs.forEach(input => input.value = 1);
      const quantityInput = document.getElementById('quantity-input');
      if (quantityInput) quantityInput.value = 1;
    } else if (state.purchaseType === 'onetime60') {
      setQuantityForMode();
    } else if (state.purchaseType === 'subscription') {
      state.quantity = 1;
      const qtyDisplayInputs = document.querySelectorAll('.qty-display');
      qtyDisplayInputs.forEach(input => input.value = 1);
      const quantityInput = document.getElementById('quantity-input');
      if (quantityInput) quantityInput.value = 1;
    }
    
    const allCards = document.querySelectorAll('.purchase-card');
    allCards.forEach(card => card.classList.remove('selected'));
    
    const selectedCard = radio.closest('.purchase-card');
    if (selectedCard) selectedCard.classList.add('selected');
    
    // Keep Juno+ benefits visible for all purchase types
    const onetimeWarning = document.getElementById('onetime-warning');
    if (onetimeWarning) {
      onetimeWarning.style.display = (state.purchaseType === 'onetime60' || state.purchaseType === 'taster') ? 'flex' : 'none';
    }
    
    if (state.selectedSize) {
      selectVariant();
    }
    updatePricing();
    
    // Sync hidden Appstle widget: set checked and fire change so Appstle updates its ready state
    const appstleInputs = getAppstleSellingPlanInputs();
    if (appstleInputs.length > 0) {
      if (state.purchaseType === 'subscription') {
        const subscriptionRadio = appstleInputs.find(function(r) { return r.value && String(r.value).trim() !== ''; });
        if (subscriptionRadio) {
          subscriptionRadio.checked = true;
          subscriptionRadio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } else {
        // onetime60 and taster: no selling plan
        const onetimeRadio = appstleInputs.find(function(r) { return !r.value || String(r.value).trim() === ''; });
        if (onetimeRadio) {
          onetimeRadio.checked = true;
          onetimeRadio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }
    
    // If no size selected, highlight size section to prompt selection
    if (!state.selectedSize) {
      highlightSizeSection();
    }

    // Dispatch state change event for banner
    window.dispatchEvent(new CustomEvent('juno:formStateChange', { 
      detail: {
        ...state,
        selectedSize: state.selectedSize,
        selectedVariant: state.selectedVariant,
        purchaseType: state.purchaseType,
        quantity: state.quantity
      }
    }));
  }

  function setQuantityForMode() {
    // Always keep cart quantity at 1 for all modes.
    // The size-specific variant itself represents the correct supply,
    // so we should not multiply the number of units for one-time orders.
    state.quantity = 1;
    const qtyDisplayInputs = document.querySelectorAll('.qty-display');
    qtyDisplayInputs.forEach(input => input.value = state.quantity);
    const quantityInput = document.getElementById('quantity-input');
    if (quantityInput) quantityInput.value = state.quantity;
  }

  // Handle quantity change (subscription only; onetime60 and taster have fixed quantity)
  function handleQuantityChange(delta) {
    if (state.purchaseType !== 'subscription') return;
    const newQty = state.quantity + delta;
    if (newQty >= 1 && newQty <= 10) {
      state.quantity = newQty;
      const qtyDisplayInputs = document.querySelectorAll('.qty-display');
      qtyDisplayInputs.forEach(input => input.value = newQty);
      const quantityInput = document.getElementById('quantity-input');
      if (quantityInput) quantityInput.value = newQty;
      updatePricing();
      
      // Dispatch state change event for banner
      window.dispatchEvent(new CustomEvent('juno:formStateChange', { 
        detail: {
          ...state,
          selectedSize: state.selectedSize,
          selectedVariant: state.selectedVariant,
          purchaseType: state.purchaseType,
          quantity: state.quantity
        }
      }));
    }
  }

  // Helper to reset button state
  function resetButtonState() {
    const btnTexts = document.querySelectorAll('.btn-text');
    const btnLoaders = document.querySelectorAll('.btn-loader');
    btnTexts.forEach(text => text.style.display = 'block');
    btnLoaders.forEach(loader => loader.style.display = 'none');
    state.isSubmitting = false;
  }

  // Validate and submit form. We build the payload ourselves and call fetch only;
  // form never submits natively, so line item properties cannot be stripped by other scripts.
  function handleFormSubmit(e) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    }
    
    if (state.isSubmitting) {
      if (DEBUG) console.warn('Form submission already in progress');
      return;
    }
    if (Date.now() - lastCartAddTime < CART_ADD_THROTTLE_MS) {
      if (DEBUG) console.warn('Add to cart throttled');
      return;
    }
    
    if (DEBUG) console.log('handleFormSubmit called', {
      selectedSize: state.selectedSize,
      selectedVariant: state.selectedVariant?.id,
      purchaseType: state.purchaseType
    });
    
    // Validate size
    if (!state.selectedSize) {
      highlightSizeSection();
      return;
    }
    
    // Name and breed are optional (they personalise the plan and emails, but a blank one
    // must not stop the add: shop_events showed it blocking). A typed name is still checked.
    const nameInput = document.getElementById('property-dog-name');
    const hasName = nameInput && nameInput.value.trim().length > 0;
    if (hasName && isNameInappropriate(nameInput.value.trim())) {
      showError('Please choose an appropriate name for your dog.');
      resetButtonState();
      return;
    }
    
    if (!state.selectedVariant || !state.selectedVariant.id) {
      showError('Please select a size and purchase option');
      resetButtonState();
      return;
    }
    
    state.isSubmitting = true;
    
    // Show loading state
    const btnTexts = document.querySelectorAll('.btn-text');
    const btnLoaders = document.querySelectorAll('.btn-loader');
    btnTexts.forEach(text => text.style.display = 'none');
    btnLoaders.forEach(loader => loader.style.display = 'flex');
    hideError();
    
    // Prepare data
    function capitalizeName(name) {
      return formatDogName(name);
    }
    
    // Read data: prefer visible inputs (user can edit), fallback to quiz data in state
    const breedInput = document.getElementById('property-dog-breed');
    let dogName = nameInput ? capitalizeName(nameInput.value.trim()) : '';
    if (!dogName && state.dogDetails?.name) dogName = state.dogDetails.name;
    
    let dogBreed = breedInput ? breedInput.value.trim() : '';
    if (!dogBreed && state.dogDetails?.breed) dogBreed = state.dogDetails.breed;
    
    // For age, issues, notes: check hidden inputs first (may have been updated), then state
    const ageInput = document.getElementById('property-dog-age');
    const issuesInput = document.getElementById('property-dog-issues');
    const notesInput = document.getElementById('property-dog-notes');
    
    let dogAge = ageInput && ageInput.value ? ageInput.value.trim() : '';
    if (!dogAge) dogAge = state.dogDetails?.age || '';
    
    const dogSize = state.selectedSize || '';
    
    let dogIssues = issuesInput && issuesInput.value ? issuesInput.value.trim() : '';
    if (!dogIssues) dogIssues = state.dogDetails?.issues || '';
    
    let dogNotes = notesInput && notesInput.value ? notesInput.value.trim() : '';
    if (!dogNotes) dogNotes = state.dogDetails?.notes || '';
    
    // Normalize age
    let normalizedAge = dogAge;
    if (dogAge) {
      const clean = dogAge.trim().toLowerCase();
      const numberMatch = clean.match(/[\d\.]+/);
      if (numberMatch) {
        const val = parseFloat(numberMatch[0]);
        let unit = 'years';
        if (clean.includes('m')) unit = val === 1 ? 'month' : 'months';
        else if (clean.includes('w')) unit = val === 1 ? 'week' : 'weeks';
        else unit = val === 1 ? 'year' : 'years';
        normalizedAge = `${val} ${unit}`;
      }
    }
    
    // Typed text goes into a raw JSON body in the Make order scenario, so a quote,
    // backslash or control character here would break that order's sync.
    const cleanProp = (v) => (typeof v === 'string' ? v.replace(/["\\\u0000-\u001f]/g, '').trim() : v);
    dogName = cleanProp(dogName);
    dogBreed = cleanProp(dogBreed);
    normalizedAge = cleanProp(normalizedAge);
    dogIssues = cleanProp(dogIssues);
    dogNotes = cleanProp(dogNotes);

    // Build properties explicitly (Dog Name, Dog Breed, plus pet details)
    // so the payload is under our control and no other script can strip them.
    let finalProperties = {};
    if (dogName) finalProperties['Dog Name'] = dogName;
    if (dogBreed) finalProperties['Dog Breed'] = dogBreed;
    if (normalizedAge) finalProperties['Dog Age'] = normalizedAge;
    if (dogSize) finalProperties['Dog Size'] = dogSize;
    if (dogIssues) finalProperties['Dog Issues'] = dogIssues;
    if (dogNotes) finalProperties['Dog Notes'] = dogNotes;
    
    // Log for verification (can be removed in production)
    if (DEBUG) console.log('Quiz data being sent to checkout:', {
      name: dogName,
      breed: dogBreed,
      age: normalizedAge,
      size: dogSize,
      issues: dogIssues,
      notes: dogNotes
    });
    
    // Update hidden inputs to ensure they match what we're sending
    const sizeInput = document.getElementById('property-dog-size');
    if (ageInput) ageInput.value = normalizedAge || '';
    if (sizeInput) sizeInput.value = dogSize || '';
    if (issuesInput) issuesInput.value = dogIssues || '';
    if (notesInput) notesInput.value = dogNotes || '';
    
    // Also ensure name and breed hidden inputs are updated
    const nameHiddenInput = document.getElementById('property-dog-name');
    const breedHiddenInput = document.getElementById('property-dog-breed');
    if (nameHiddenInput) nameHiddenInput.value = dogName || '';
    if (breedHiddenInput) breedHiddenInput.value = dogBreed || '';
    
    // Validate variant ID
    if (!state.selectedVariant || !state.selectedVariant.id) {
      console.error('No variant selected or variant ID missing');
      showError('Please select a size and purchase option');
      resetButtonState();
      return;
    }
    
    // Cart should always receive a single unit; the chosen variant/plan
    // already encodes the correct supply and billing terms.
    let cartQuantity = 1;
    
    const cartItem = {
      id: state.selectedVariant.id,
      quantity: cartQuantity,
      properties: finalProperties
    };
    
    // sellingPlanId from the currently checked Appstle radio (dynamic)
    var sellingPlanId = getCheckedAppstleSellingPlanId();
    if (state.purchaseType === 'subscription') {
      var numericPlanId = (sellingPlanId != null && sellingPlanId !== '')
        ? parseInt(sellingPlanId, 10)
        : (state.selectedSellingPlan != null ? state.selectedSellingPlan : (getSubscriptionPlanId() != null ? getSubscriptionPlanId() : null));
      if (numericPlanId != null && !isNaN(numericPlanId) && numericPlanId > 0) {
        state.selectedSellingPlan = numericPlanId;
        cartItem.selling_plan = numericPlanId;
        var planInputSub = document.getElementById('selling-plan-input');
        if (planInputSub) planInputSub.value = numericPlanId;
      } else {
        console.error('No subscription plan ID available');
        showError('Subscription is not available for this product option. Please contact support.');
        resetButtonState();
        return;
      }
    } else {
      state.selectedSellingPlan = null;
      if (cartItem.selling_plan !== undefined) delete cartItem.selling_plan;
    }
    
    const formData = { items: [cartItem] };
    
    if (DEBUG) console.log('Cart item prepared:', {
      variantId: cartItem.id,
      quantity: cartItem.quantity,
      purchaseType: state.purchaseType
    });
    if (DEBUG) console.log('Submitting to cart');
    if (DEBUG) console.log('Selected variant:', state.selectedVariant?.id);
    if (DEBUG) console.log('Selected selling plan:', state.selectedSellingPlan?.id);
    
    // Safety timeout to reset button if request hangs
    const resetTimeout = setTimeout(() => {
      console.warn('Cart submission timeout - resetting button state');
      showError('Request timed out. Please try again.');
      resetButtonState();
    }, 15000);
    
    // Persist pet data so it's remembered when user returns
    savePetDataToStorage();
    
    // Submit to cart using direct fetch (more reliable)
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json'
      },
      body: JSON.stringify(formData)
    })
    .then(async response => {
      // Check if response is ok
      if (!response.ok) {
        let errorMessage = 'Network response was not ok';
        try {
          const err = await response.json();
          errorMessage = err.description || err.message || errorMessage;
        } catch (e) {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }
      
      // Try to parse JSON response
      try {
        return await response.json();
      } catch (e) {
        // If response is not JSON, might be HTML error page
        throw new Error('Invalid response format. Please try again.');
      }
    })
    .then(data => {
      clearTimeout(resetTimeout);
      if (DEBUG) console.log('Cart add response received');
      
      // Shopify's /cart/add.js returns a cart object on success (with items array)
      // Errors can be in response body even with 200 status: { status: 422, description: "Error" }
      
      if (!data) {
        console.error('Cart add returned no data');
        showError('No response from server. Please try again.');
        resetButtonState();
        return;
      }
      
      // Check for Shopify error format in response body
      // Error format: { status: 422, description: "Error message" } or { message: "Error" }
      if (data.status && (data.status !== 200 && data.status !== 201)) {
        const errorMsg = data.description || data.message || 'Unable to add to cart. Please try again.';
        console.error('Cart add error response:', data);
        showError(errorMsg);
        resetButtonState();
        return;
      }
      
      // Success: Shopify returns cart object with items array
      // Check for items array as primary success indicator
      if (data.items && Array.isArray(data.items)) {
        lastCartAddTime = Date.now();
        if (DEBUG) console.log('Cart add successful');
        // GTM dataLayer - add_to_cart for GA4, Meta Pixel, etc. (enhanced with product details)
        var pricePence = state.selectedVariant?.price || 0;
        var priceValue = pricePence / 100;
        var qty = 1;
        var item = data.items[0];
        var currency = (window.theme && window.theme.current_iso_code) || 'GBP';
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'add_to_cart',
          form_id: 'juno-product-form',
          click_source: window._junoWidgetClick ? 'widget' : 'main_form',
          purchase_type: state.purchaseType || 'subscription',
          value: priceValue * qty,
          currency: currency,
          product_name: (window.productData && window.productData.title) || (item && item.title) || '',
          product_id: (window.productData && window.productData.id) || (item && item.product_id) || '',
          variant_id: state.selectedVariant?.id || (item && item.variant_id) || '',
          quantity: qty,
          items: data.items
        });
        window._junoWidgetClick = false;
        // Add 500ms delay to allow GTM tags to fire before redirect
        setTimeout(function() {
          window.location.href = '/cart';
        }, 500);
        return;
      }
      
      // If no items array but also no error status, might be success
      // (some edge cases or different response formats)
      if (!data.status || data.status === 200 || data.status === 201) {
        lastCartAddTime = Date.now();
        if (DEBUG) console.log('Redirecting to checkout');
        // GTM dataLayer - add_to_cart (enhanced)
        var pricePence = state.selectedVariant?.price || 0;
        var qty = 1;
        var currency = (window.theme && window.theme.current_iso_code) || 'GBP';
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'add_to_cart',
          form_id: 'juno-product-form',
          click_source: window._junoWidgetClick ? 'widget' : 'main_form',
          purchase_type: state.purchaseType || 'subscription',
          value: (pricePence / 100) * qty,
          currency: currency,
          product_name: window.productData && window.productData.title ? window.productData.title : '',
          product_id: window.productData && window.productData.id ? window.productData.id : '',
          variant_id: state.selectedVariant?.id || '',
          quantity: qty
        });
        window._junoWidgetClick = false;
        // Add 500ms delay to allow GTM tags to fire before redirect
        setTimeout(function() {
          window.location.href = '/cart';
        }, 500);
        return;
      }
      
      // Fallback: unexpected response format
      console.warn('Unexpected cart add response format:', data);
      showError('Unexpected response. Please try again.');
      resetButtonState();
    })
    .catch(error => {
      clearTimeout(resetTimeout);
      console.error('Cart add error:', error);
      const errorMessage = error?.message || error?.toString() || 'Unable to add to cart. Please try again.';
      showError(errorMessage);
      resetButtonState();
    });
  }

  // Initialize
  function init() {
    if (!productData || !productData.variants) {
      console.error('Product data not available');
      return;
    }
    
    const onetimeSubtitle = document.querySelector('.onetime-subtitle');
    if (onetimeSubtitle) {
      onetimeSubtitle.innerHTML = `2 months supply.<br>Ships next business day`;
    }
    
    // Set up input checkmarks
    const dogNameInput = document.getElementById('property-dog-name');
    const dogBreedInput = document.getElementById('property-dog-breed');
    initBreedDropdown();
    
    function updateCheckmark(input) {
      const inputGroup = input?.closest('.pet-input-group');
      if (input && input.value.trim().length > 0) {
        input.classList.add('filled');
        if (inputGroup) inputGroup.classList.add('has-filled');
      } else {
        input.classList.remove('filled');
        if (inputGroup) inputGroup.classList.remove('has-filled');
      }
    }
    
    if (dogNameInput) {
      dogNameInput.addEventListener('input', () => {
        updateCheckmark(dogNameInput);
        state.dogDetails = state.dogDetails || {};
        state.dogDetails.name = dogNameInput.value.trim() ? dogNameInput.value.trim() : null;
        if (state.selectedSize) updateDosageMessage();
        window.dispatchEvent(new CustomEvent('juno:formStateChange', { detail: { ...state } }));
      });
      dogNameInput.addEventListener('blur', () => {
        updateCheckmark(dogNameInput);
        state.dogDetails = state.dogDetails || {};
        state.dogDetails.name = dogNameInput.value.trim() ? dogNameInput.value.trim() : null;
        if (state.selectedSize) updateDosageMessage();
        window.dispatchEvent(new CustomEvent('juno:formStateChange', { detail: { ...state } }));
        savePetDataToStorage();
        window.junoUpdatePersonalization && window.junoUpdatePersonalization();
        if (!state.selectedSize && dogNameInput.value.trim() && dogBreedInput?.value?.trim()) {
          highlightSizeSection();
        }
      });
    }
    if (dogBreedInput) {
      dogBreedInput.addEventListener('input', () => {
        updateCheckmark(dogBreedInput);
        state.dogDetails = state.dogDetails || {};
        state.dogDetails.breed = dogBreedInput.value.trim() ? dogBreedInput.value.trim() : null;
        window.dispatchEvent(new CustomEvent('juno:formStateChange', { detail: { ...state } }));
      });
      dogBreedInput.addEventListener('blur', () => {
        updateCheckmark(dogBreedInput);
        state.dogDetails = state.dogDetails || {};
        state.dogDetails.breed = dogBreedInput.value.trim() ? dogBreedInput.value.trim() : null;
        window.dispatchEvent(new CustomEvent('juno:formStateChange', { detail: { ...state } }));
        savePetDataToStorage();
        window.junoUpdatePersonalization && window.junoUpdatePersonalization();
        if (!state.selectedSize && dogNameInput?.value?.trim() && dogBreedInput.value.trim()) {
          highlightSizeSection();
        }
      });
    }
    
    // Expose for widget to trigger update when user blurs from widget name/breed
    window.junoUpdatePersonalization = function() {
      updatePersonalizedTitles();
      updateRestOfPagePersonalization();
    };
    
    // Load and apply quiz data
    const quizDetails = loadDogDetails();
    prefillInputs(quizDetails);
    // Size is preselected further down, once the size button handler is bound.

    // Sync dog name and breed from inputs to state (for widget and below-splash personalisation)
    state.dogDetails = state.dogDetails || {};
    if (dogNameInput && dogNameInput.value.trim()) {
      state.dogDetails.name = dogNameInput.value.trim();
    }
    if (dogBreedInput && dogBreedInput.value.trim()) {
      state.dogDetails.breed = dogBreedInput.value.trim();
    }
    
    // Only update personalized titles and rest-of-page (Juno Standard for breed, name everywhere) on load when data came from quiz (pre-filled)
    if (quizDetails && Object.values(quizDetails).some(function(v) { return v && String(v).trim(); })) {
      updatePersonalizedTitles();
      updateRestOfPagePersonalization();
    }
    
    // Set up event delegation on form container
    const formContainer = document.querySelector('.juno-subscription-form-v3');
    if (!formContainer) {
      console.error('Form container not found');
      return;
    }
    
    // Update personalized titles only when user has scrolled past the details section (not live while typing)
    const sizeSection = formContainer.querySelector('.size-section');
    const splashThreshold = sizeSection
      ? (sizeSection.getBoundingClientRect().top + window.scrollY) : 400;
    function onScrollPastSplash() {
      if (window.scrollY >= splashThreshold) {
        updatePersonalizedTitles();
        updateRestOfPagePersonalization();
      }
    }
    window.addEventListener('scroll', onScrollPastSplash, { passive: true });
    
    // Click delegation for size buttons and quantity buttons
    formContainer.addEventListener('click', (e) => {
      const sizeBtn = e.target.closest('.size-btn');
      if (sizeBtn) {
        e.preventDefault();
        e.stopPropagation();
        handleSizeClick(sizeBtn);
        return;
      }
      
      const qtyMinus = e.target.closest('.qty-minus');
      if (qtyMinus) {
        e.preventDefault();
        handleQuantityChange(-1);
        return;
      }
      
      const qtyPlus = e.target.closest('.qty-plus');
      if (qtyPlus) {
        e.preventDefault();
        handleQuantityChange(1);
        return;
      }
      
      const addToCartBtn = e.target.closest('.add-to-cart-btn');
      if (addToCartBtn) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        if (DEBUG) console.log('Add to cart button clicked');
        handleFormSubmit(e);
        return;
      }

      const benefitsToggle = e.target.closest('.benefits-text-toggle');
      if (benefitsToggle) {
        e.preventDefault();
        // handled by global capture listener
        return;
      }
    });
    
    // Change delegation for purchase radios
    formContainer.addEventListener('change', (e) => {
      if (e.target.classList.contains('purchase-radio')) {
        handlePurchaseTypeChange(e.target);
      }
    });
    
    // Form submit handler: use capture so we run first; we prevent default and use fetch only,
    // so no other script (e.g. Appstle) can intercept or strip our properties.
    const productForm = document.getElementById('juno-product-form');
    if (productForm) {
      productForm.addEventListener('submit', handleFormSubmit, true);
    }

    // When user returns via Back button, page may be restored from bfcache with button still in loading state
    window.addEventListener('pageshow', function(event) {
      if (event.persisted) {
        resetButtonState();
      }
    });

    // Expose for floating widget to trigger checkout directly (avoids synthetic click double-tap issue)
    window.junoSubmitSubscriptionForm = function() { handleFormSubmit(null); };
    
    // One-time warning switch (back to Juno+ subscription)
    const onetimeWarningSwitch = document.querySelector('.onetime-warning-switch');
    if (onetimeWarningSwitch) {
      onetimeWarningSwitch.addEventListener('click', () => {
        const subscriptionRadio = document.getElementById('purchase-type-subscription');
        if (subscriptionRadio) {
          subscriptionRadio.checked = true;
          subscriptionRadio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }

    // Taster anchor: handled via click delegation on formContainer
    
    // Initialize selling plan
    const planId = getSubscriptionPlanId();
    if (planId) state.selectedSellingPlan = planId;
    
    // Set default variant for form (user must explicitly select size via size buttons)
    if (productData.variants?.length > 0) {
      state.selectedVariant = productData.variants[0];
      const variantInput = document.getElementById('variant-id-input');
      if (variantInput) variantInput.value = productData.variants[0].id;
    }
    
    // Set default subscription card as selected
    const subscriptionCard = document.querySelector('.subscription-card');
    if (subscriptionCard) subscriptionCard.classList.add('selected');

    // Preselect the size from the link / quiz / saved pet data. This has to come after the
    // click delegation above (so the click is handled) and after the default variant (so the
    // default does not overwrite it). No size given leaves nothing selected, as before.
    autoSelectSize(quizDetails);

    // Always keep Juno+ benefits section visible, but collapse extras for one-time modes
    if (state.purchaseType === 'onetime60' || state.purchaseType === 'taster') {
      const benefitsSection = document.querySelector('.subscription-benefits-section');
      if (benefitsSection) {
        const extraWrapper = benefitsSection.querySelector('.benefits-extra-wrapper');
        const toggleBtn = benefitsSection.querySelector('.benefits-text-toggle');
        if (extraWrapper) extraWrapper.style.display = 'none';
        if (toggleBtn) toggleBtn.textContent = 'View 4 more benefits ▼';
      }
    }
    
    updatePricing();
    
    // Wait for Appstle widget to load then sync Juno cards and set state.selectedSellingPlan
    function trySyncAppstleAfterLoad(attempt) {
      var maxAttempts = 15;
      var delayMs = 200;
      if (attempt >= maxAttempts) return;
      var appstleInputsInit = getAppstleSellingPlanInputs();
      if (appstleInputsInit.length > 0) {
        if (state.purchaseType === 'subscription') {
          var subRadioInit = appstleInputsInit.find(function(r) { return r.value && String(r.value).trim() !== ''; });
          if (subRadioInit) {
            subRadioInit.checked = true;
            subRadioInit.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
        var checkedId = getCheckedAppstleSellingPlanId();
        if (checkedId) state.selectedSellingPlan = parseInt(checkedId, 10);
        else if (state.purchaseType === 'subscription') state.selectedSellingPlan = getSubscriptionPlanId();
        return;
      }
      setTimeout(function() { trySyncAppstleAfterLoad(attempt + 1); }, delayMs);
    }
    setTimeout(function() { trySyncAppstleAfterLoad(0); }, 100);
    
    // Dispatch initial state change event for banner
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('juno:formStateChange', { 
        detail: {
          ...state,
          selectedSize: state.selectedSize,
          selectedVariant: state.selectedVariant,
          purchaseType: state.purchaseType,
          quantity: state.quantity
        }
      }));
    }, 100);

    initConversionWidget();
  }

  // Global capture listener so we always catch toggle clicks,
  // even if other scripts stop propagation.
  document.addEventListener('click', function(e) {
    const btn = e.target.closest && e.target.closest('.benefits-text-toggle');
    if (!btn) return;
    e.preventDefault();
    const section = btn.closest('.subscription-benefits-section');
    if (!section) return;
    const extra = section.querySelector('.benefits-extra-wrapper');
    if (!extra) return;
    const isHidden = extra.style.display === 'none' || extra.style.display === '';
    extra.style.display = isHidden ? 'block' : 'none';
    btn.textContent = isHidden ? 'Hide benefits ▲' : 'View 4 more benefits ▼';
  }, true);

  function initConversionWidget() {
    const widget = document.getElementById('prescription-widget');
    const stateSelect = document.getElementById('widget-state-select');
    const stateResult = document.getElementById('widget-state-result');
    const widgetTitle = document.getElementById('widget-title');
    const widgetResultName = document.getElementById('widget-result-name');
    const widgetResultSize = document.getElementById('widget-result-size');
    const widgetDailyCost = document.getElementById('widget-daily-cost');
    const widgetBilledText = document.getElementById('widget-billed-text');
    const weightBtns = document.querySelectorAll('.widget_weight_btn');
    const ctaBtn = document.getElementById('widget-cta-btn');
    const petDetailsEl = document.getElementById('widget-pet-details');
    const dogNameInput = document.getElementById('widget-dog-name');
    const dogBreedInput = document.getElementById('widget-dog-breed');
    const ctaSubmitBtn = document.getElementById('widget-cta-submit');
    const closeBtn = document.getElementById('prescription-widget-close');
    const DISMISS_KEY = 'prescription_widget_dismissed';

    if (!widget || !stateSelect || !stateResult) return;

    // Limit widget to Juno Daily product page / shop context only
    const allowedHandles = ['juno-daily-supplement-all-in-one'];
    const currentHandle = (typeof productHandle !== 'undefined' && productHandle) ? String(productHandle) : null;
    const path = window.location && window.location.pathname ? window.location.pathname : '';
    const isAllowedPath =
      path.indexOf('/products/juno-daily-supplement-all-in-one') === 0 ||
      path.indexOf('/products/juno-daily') === 0;
    const isAllowed =
      (currentHandle && allowedHandles.indexOf(currentHandle) !== -1) ||
      isAllowedPath;

    if (!isAllowed) {
      widget.classList.remove('is_visible');
      widget.setAttribute('aria-hidden', 'true');
      return;
    }

    function formatMoney(cents) {
      return '£' + (cents / 100).toFixed(2);
    }

    function isDismissed() {
      try {
        return sessionStorage.getItem(DISMISS_KEY) === '1';
      } catch (e) {
        return false;
      }
    }

    function setDismissed() {
      try {
        sessionStorage.setItem(DISMISS_KEY, '1');
      } catch (e) {}
    }

    function getDogName() {
      const st = window.subscriptionFormState;
      let name = st?.dogDetails?.name;
      if (!name) {
        const input = document.getElementById('property-dog-name');
        name = input?.value?.trim() || '';
      }
      return name;
    }

    function updatePersonalisedTitle() {
      const dogName = getDogName();
      if (widgetTitle) {
        widgetTitle.textContent = dogName
          ? 'Juno Daily for ' + formatDogName(String(dogName))
          : 'Find your dog\'s daily dose';
      }
    }

    function getScrollThreshold() {
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      return Math.max(200, docHeight * 0.25);
    }

    let userDismissedToPill = false;

    function showWidget(shouldExpand) {
      widget.classList.add('is_visible');
      widget.setAttribute('aria-hidden', 'false');
      if (shouldExpand !== false) {
        widget.classList.remove('is_minimised');
      }
    }

    function hideWidget() {
      widget.classList.remove('is_visible');
      widget.setAttribute('aria-hidden', 'true');
    }

    function updatePillText() {
      const pillText = document.getElementById('prescription-widget-pill-text');
      if (!pillText) return;
      const st = window.subscriptionFormState;
      const hasSize = st?.selectedSize;
      const dogName = getDogName();
      const capName = dogName
        ? formatDogName(String(dogName))
        : null;
      if (hasSize) {
        pillText.textContent = capName
          ? 'Start ' + capName + '\'s Gut Transformation'
          : 'Start your dog\'s gut transformation';
      } else {
        pillText.textContent = capName
          ? 'Find ' + capName + '\'s daily dose'
          : 'Find your dog\'s daily dose';
      }
    }

    function formatDailyCost(pence) {
      if (pence >= 100) {
        const pounds = (pence / 100).toFixed(2);
        return 'Only £' + pounds + ' per day';
      }
      return 'Only ' + pence + 'p per day';
    }

    function truncateResultTitle(text) {
      if (!text) return '';
      const width = window.innerWidth || document.documentElement.clientWidth || 1024;
      let maxLength = 40;
      if (width <= 480) {
        maxLength = 26;
      } else if (width <= 768) {
        maxLength = 32;
      }
      if (text.length <= maxLength) return text;
      const shortened = text.slice(0, maxLength);
      const lastSpace = shortened.lastIndexOf(' ');
      const safe = lastSpace > 0 ? shortened.slice(0, lastSpace) : shortened;
      return safe.trimEnd() + '…';
    }

    function showResultState() {
      stateSelect.style.display = 'none';
      stateResult.style.display = 'block';
      resetPetDetailsInWidget();
      const st = window.subscriptionFormState;
      if (!st?.selectedVariant || !st?.selectedSize) return;

      const basePrice = st.selectedVariant.price;
      const subPrice = Math.round(basePrice * 0.8);
      const qty = st.quantity || 1;
      const billedTotal = subPrice * qty;
      const daysSupply = 60;
      const dailyPence = Math.round(billedTotal / daysSupply);

      if (widgetResultName) {
        const dogName = getDogName();
        const capName = dogName
          ? formatDogName(String(dogName))
          : null;
        widgetResultName.textContent = capName ? 'Juno Daily for ' + capName : 'Juno Daily';
      }
      if (widgetResultSize) widgetResultSize.textContent = '';
      if (widgetDailyCost) widgetDailyCost.textContent = formatDailyCost(dailyPence);
      if (widgetBilledText) widgetBilledText.textContent = formatMoney(billedTotal) + ' billed every 2 months';

      // Populate dosage line
      const dosageScoops = document.getElementById('widget-dosage-scoops');
      const dosageSupply = document.getElementById('widget-dosage-supply');
      if (dosageScoops && dosageSupply) {
        const scoopsMap = { 'Small': '½', 'Medium': '1', 'Large': '1½', 'Extra Large': '2' };
        const scoopsText = scoopsMap[st.selectedSize] || '1';
        const scoopWord = (st.selectedSize === 'Small' || st.selectedSize === 'Medium') ? 'scoop' : 'scoops';
        dosageScoops.textContent = scoopsText + ' ' + scoopWord + '/day';
        dosageSupply.textContent = '60-day supply';
      }

      // Populate savings pill
      const savingsPill = document.getElementById('widget-savings-pill');
      if (savingsPill) {
        savingsPill.textContent = 'Save 20%';
      }

      if (ctaBtn) {
        const dogName = getDogName();
        const capName = dogName
          ? formatDogName(String(dogName))
          : null;
        const priceStr = formatMoney(billedTotal);
        ctaBtn.textContent = capName
          ? 'Subscribe for ' + capName + ' - ' + priceStr
          : 'Subscribe & Save - ' + priceStr;
      }
    }

    function showSelectState() {
      stateSelect.style.display = 'block';
      stateResult.style.display = 'none';
      weightBtns.forEach(b => b.classList.remove('is_selected'));
      resetPetDetailsInWidget();
    }

    function resetPetDetailsInWidget() {
      if (petDetailsEl) petDetailsEl.style.display = 'none';
      if (ctaBtn) ctaBtn.style.display = '';
    }

    function syncFromMainForm() {
      const st = window.subscriptionFormState;
      const mainName = document.getElementById('property-dog-name');
      const mainBreed = document.getElementById('property-dog-breed');
      if (dogNameInput && mainName) dogNameInput.value = mainName.value || '';
      if (dogBreedInput && mainBreed) dogBreedInput.value = mainBreed.value || '';
      updatePersonalisedTitle();
      updatePillText();
      if (st?.selectedSize) {
        showResultState();
        weightBtns.forEach(b => {
          b.classList.toggle('is_selected', b.dataset.size === st.selectedSize);
        });
      } else {
        showSelectState();
      }
    }

    const checkScroll = () => {
      const threshold = getScrollThreshold();
      if (window.scrollY >= threshold) {
        showWidget(!userDismissedToPill);
        syncFromMainForm();
      } else {
        hideWidget();
      }
    };
    window.addEventListener('scroll', checkScroll, { passive: true });
    checkScroll();

    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        userDismissedToPill = true;
        setDismissed();
        widget.classList.add('is_minimised');
        updatePillText();
        stateSelect.style.display = 'none';
        stateResult.style.display = 'none';
      });
    }

    const pillBtn = document.getElementById('prescription-widget-pill');
    if (pillBtn) {
      pillBtn.addEventListener('click', () => {
        try { sessionStorage.removeItem(DISMISS_KEY); } catch (e) {}
        widget.classList.remove('is_minimised');
        syncFromMainForm();
      });
    }

    weightBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const size = btn.dataset.size;
        const mainSizeBtn = document.querySelector('.juno-subscription-form-v3 .size-btn[data-size="' + size + '"]');
        if (mainSizeBtn) mainSizeBtn.click();
        showResultState();
        weightBtns.forEach(b => b.classList.remove('is_selected'));
        btn.classList.add('is_selected');
      });
    });

    if (ctaBtn) {
      ctaBtn.addEventListener('click', () => {
        const mainName = document.getElementById('property-dog-name');
        const hasName = mainName?.value?.trim() || '';
        // Name and breed are optional: go straight to the add, the main form checks size.
        // Track widget CTA click
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'widget_cta_click',
          widget_id: 'prescription-widget',
          click_source: 'widget',
          dog_name: hasName,
          purchase_type: state.purchaseType || 'subscription'
        });
        // Flag so add_to_cart event knows it came from widget
        window._junoWidgetClick = true;
        const mainBtn = document.getElementById('add-to-cart-btn') || document.querySelector('.juno-subscription-form-v3 .add-to-cart-btn');
        if (mainBtn) mainBtn.click();
      });
    }

    if (dogNameInput) {
      dogNameInput.addEventListener('input', () => { dogNameInput.style.borderColor = ''; });
      dogNameInput.addEventListener('keydown', (e) => {
        if (e.key === 'Tab' && !e.shiftKey && dogBreedInput) {
          e.preventDefault();
          dogBreedInput.focus();
        }
      });
    }
    if (dogBreedInput) {
      dogBreedInput.addEventListener('input', () => { dogBreedInput.style.borderColor = ''; });
    }

    function syncWidgetToMain() {
      const name = dogNameInput?.value?.trim() || '';
      const breed = dogBreedInput?.value?.trim() || '';
      const mainName = document.getElementById('property-dog-name');
      const mainBreed = document.getElementById('property-dog-breed');
      if (mainName) mainName.value = name;
      if (mainBreed) mainBreed.value = breed;
      const st = window.subscriptionFormState || {};
      st.dogDetails = st.dogDetails || {};
      st.dogDetails.name = name || null;
      st.dogDetails.breed = breed || null;
      window.subscriptionFormState = st;
      window.dispatchEvent(new CustomEvent('juno:formStateChange', { detail: { ...st } }));
      const nameGroup = mainName?.closest('.pet-input-group');
      const breedGroup = mainBreed?.closest('.pet-input-group');
      if (mainName) {
        mainName.classList.toggle('filled', name.length > 0);
        if (nameGroup) nameGroup.classList.toggle('has-filled', name.length > 0);
      }
      if (mainBreed) {
        mainBreed.classList.toggle('filled', breed.length > 0);
        if (breedGroup) breedGroup.classList.toggle('has-filled', breed.length > 0);
      }
      savePetDataToStorage();
      window.junoUpdatePersonalization && window.junoUpdatePersonalization();
    }
    if (dogNameInput) dogNameInput.addEventListener('blur', syncWidgetToMain);
    if (dogBreedInput) dogBreedInput.addEventListener('blur', syncWidgetToMain);

    if (ctaSubmitBtn) {
      ctaSubmitBtn.addEventListener('click', () => {
        const name = dogNameInput?.value?.trim() || '';
        const breed = dogBreedInput?.value?.trim() || '';
        const mainName = document.getElementById('property-dog-name');
        const mainBreed = document.getElementById('property-dog-breed');
        // Only copy what was typed here: a blank widget field must not clear the main form.
        if (mainName && name) mainName.value = name;
        if (mainBreed && breed) mainBreed.value = breed;
        if (window.subscriptionFormState?.dogDetails) {
          if (name) window.subscriptionFormState.dogDetails.name = name;
          if (breed) window.subscriptionFormState.dogDetails.breed = breed;
        }
        if (typeof window.junoSubmitSubscriptionForm === 'function') {
          window.junoSubmitSubscriptionForm();
        } else {
          const mainBtn = document.getElementById('add-to-cart-btn') || document.querySelector('.juno-subscription-form-v3 .add-to-cart-btn');
          if (mainBtn) mainBtn.click();
        }
      });
    }

    window.addEventListener('juno:formStateChange', () => {
      if (window.scrollY >= getScrollThreshold()) {
        syncFromMainForm();
      }
    });

    syncFromMainForm();
  }

  // Initialize on DOM ready
  if (typeof window.domReady === 'function') {
    window.domReady(init);
  } else {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  }
})();
